import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

import { cn } from '@/lib/utils';

/** A rectangular glyph shaped to the given aspect ratio (e.g. "16:9").
 *  Outlined with a faint tint — reads as a clean frame, stays crisp. */
export const AspectIcon: React.FC<{ ratio: string; size?: number }> = ({ ratio, size = 16 }) => {
    const parts = String(ratio).split(/[:/]/).map((n) => parseFloat(n));
    const w = parts[0] || 1;
    const h = parts[1] || 1;
    const pad = 1.5;
    const avail = size - pad * 2;
    let rw: number;
    let rh: number;
    if (w >= h) { rw = avail; rh = (avail * h) / w; } else { rh = avail; rw = (avail * w) / h; }
    const x = (size - rw) / 2;
    const y = (size - rh) / 2;
    return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} fill="none" shapeRendering="geometricPrecision">
            <rect x={x} y={y} width={rw} height={rh} fill="currentColor" fillOpacity={0.1} stroke="currentColor" strokeWidth={1.75} />
        </svg>
    );
};

/**
 * Aspect-ratio control. Collapsed: the CURRENT ratio as a circular frame icon +
 * a dark badge (16:9 / 1:1 …). Click it and the other options slide open
 * horizontally; picking one selects it and collapses. Click-outside closes.
 */
export const AspectRatioControl: React.FC<{
    options: Array<{ value: string; label?: string }>;
    value?: string;
    onChange: (v: string) => void;
    /** Lets a cramped toolbar make room while the options are slid open. */
    onOpenChange?: (open: boolean) => void;
}> = ({ options, value, onChange, onOpenChange }) => {
    const [open, setOpen] = useState(false);
    const ref = React.useRef<HTMLDivElement>(null);
    useEffect(() => { onOpenChange?.(open); }, [open, onOpenChange]);
    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onDown, true);
        return () => document.removeEventListener('mousedown', onDown, true);
    }, [open]);
    if (!options.length) return null;
    const current = options.find((o) => String(o.value) === String(value)) || options[0];
    const others = options.filter((o) => String(o.value) !== String(current.value));
    const ICON = 34;
    const GAP = 4;
    const othersWidth = others.length * (ICON + GAP);
    const iconBtn = (opt: { value: string; label?: string }, active: boolean, onClick: () => void) => (
        <div key={opt.value} className="relative flex shrink-0">
            <button
                type="button"
                onClick={onClick}
                title={opt.label || opt.value}
                aria-pressed={active}
                className={cn(
                    'flex size-[34px] items-center justify-center rounded-full border bg-background p-0 transition-colors hover:border-input hover:text-foreground',
                    active ? 'text-muted-foreground' : 'text-muted-foreground/70',
                )}
            >
                <AspectIcon ratio={opt.value} size={17} />
            </button>
            <span
                className={cn(
                    'pointer-events-none absolute -bottom-1.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-sm border-[1.5px] border-background px-1 py-px text-[8.5px] leading-[1.3] font-medium tracking-[0.02em]',
                    active ? 'bg-foreground text-background' : 'bg-muted text-muted-foreground',
                )}
            >
                {opt.value}
            </span>
        </div>
    );
    return (
        <div ref={ref} className="flex items-center">
            <motion.div
                initial={false}
                animate={{ width: open ? othersWidth : 0, opacity: open ? 1 : 0, marginRight: open ? GAP : 0 }}
                transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                className="flex items-center overflow-hidden py-[7px]"
                style={{ gap: `${GAP}px` }}
            >
                {others.map((opt) => iconBtn(opt, false, () => { onChange(opt.value); setOpen(false); }))}
            </motion.div>
            <div className="flex items-center py-[7px]">
                {iconBtn(current, true, () => setOpen((o) => !o))}
            </div>
        </div>
    );
};
